import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CalendarOff, Link2, Pencil, Search, UserMinus, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/Layout";
import { StaffDialog } from "@/components/schedule/StaffDialog";
import { ServicesPanel } from "@/components/schedule/ServicesPanel";
import { api, formatApiError } from "@/lib/api";
import { dateKey, hoursSummary } from "@/lib/cal";

const StaffCard = ({ s, account, onEdit, onRemove }) => {
  const today = dateKey(new Date());
  const upcoming = s.time_off.filter((t) => t.end_date >= today);
  return (
    <div className="flex items-start gap-3 rounded-xl border bg-card p-4" data-testid={`staff-card-${s.id}`}>
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full font-display font-bold text-white" style={{ backgroundColor: s.color }}>{s.name[0]?.toUpperCase()}</div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold">{s.name}</div>
        {s.role_title && <div className="truncate text-xs text-muted-foreground">{s.role_title}</div>}
        <div className="mt-1 text-xs">{hoursSummary(s.working_hours)}</div>
        {account && (
          <div className="mt-1 flex items-center gap-1 truncate text-xs text-primary" data-testid={`staff-account-${s.id}`}>
            <Link2 className="h-3 w-3 shrink-0" /> {account.email}
          </div>
        )}
        {upcoming.length > 0 && (
          <div className="mt-1 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
            <CalendarOff className="h-3 w-3" /> {upcoming[0].start_date} → {upcoming[0].end_date}{upcoming.length > 1 && ` (+${upcoming.length - 1})`}
          </div>
        )}
      </div>
      <div className="flex gap-1">
        <Button size="icon" variant="ghost" onClick={() => onEdit(s)} data-testid={`staff-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" onClick={() => onRemove(s)} className="text-muted-foreground hover:text-destructive" data-testid={`staff-remove-${s.id}`}><UserMinus className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

const StaffPanel = () => {
  const [staff, setStaff] = useState([]);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState({ open: false, member: null });
  const [users, setUsers] = useState([]);
  const load = () => api.get("/staff").then((r) => setStaff(r.data));
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)); }, []);
  const userById = Object.fromEntries(users.map((u) => [u.id, u]));

  const remove = async (s) => {
    if (!window.confirm(`Scoți pe ${s.name} din calendar? Programările existente rămân.`)) return;
    try { await api.delete(`/staff/${s.id}`); toast.success("Coleg scos din calendar"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const shown = staff.filter((s) => `${s.name} ${s.role_title}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="space-y-4" data-testid="staff-panel">
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Caută în ${staff.length} colegi...`} className="bg-card pl-9" data-testid="staff-search-input" />
        </div>
        <Button onClick={() => setDlg({ open: true, member: null })} data-testid="add-staff-button"><UserPlus className="h-4 w-4" /> Adaugă coleg</Button>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="staff-list">
        {shown.map((s) => <StaffCard key={s.id} s={s} account={userById[s.user_id]} onEdit={(m) => setDlg({ open: true, member: m })} onRemove={remove} />)}
      </div>
      {!staff.length && <p className="text-sm text-muted-foreground">Niciun coleg în calendar încă.</p>}
      <StaffDialog open={dlg.open} member={dlg.member} users={users} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))} onSaved={load} />
    </div>
  );
};

export default function ScheduleSettings() {
  return (
    <div data-testid="schedule-settings-page">
      <Link to="/programari" className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="settings-back-link"><ArrowLeft className="h-4 w-4" /> Programări</Link>
      <PageHeader title="Personal & servicii" subtitle="Echipa din calendar, programul de lucru, concediile și serviciile." />
      <Tabs defaultValue="personal">
        <TabsList className="mb-5">
          <TabsTrigger value="personal" data-testid="settings-tab-staff">Personal</TabsTrigger>
          <TabsTrigger value="servicii" data-testid="settings-tab-services">Servicii</TabsTrigger>
        </TabsList>
        <TabsContent value="personal"><StaffPanel /></TabsContent>
        <TabsContent value="servicii"><ServicesPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
